import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  addDays,
  buildFieldStaffScheduleRows,
  coerceValidDay,
  dateKey,
  dispatchLeftPx,
  dispatchWidthPx,
  DISPATCH_END_HOUR,
  DISPATCH_PX_PER_HOUR,
  DISPATCH_ROW_HEIGHT,
  DISPATCH_START_HOUR,
  DISPATCH_TIMELINE_WIDTH,
  FIELD_STAFF_PORTAL_ROLES,
  formatMinutesLabel,
  isSameDay,
  layoutTimedVisits,
  leaveBlocksForDate,
  sanitizeLeaveBlocks,
  sanitizeVisits,
  startOfDay,
  visitPlacementDate,
} from "@/components/field-calendar/fieldCalendarUtils";
import { singaporeCalendarDayBounds } from "@shared/timezone";
import { blockClassForLeave } from "@/components/field-calendar/visitBlockStyles";
import {
  DASHBOARD_SCHEDULE_LEGEND,
  dashboardScheduleBarClass,
} from "./dashboardScheduleBarStyles";
import {
  HIDE_EMPTY_TECHNICIANS_LABEL,
  SHOW_EMPTY_TECHNICIANS_LABEL,
  presentTodayTechnicianRows,
} from "@/components/field-calendar/todayTechnicianPresentation";
import { ChevronLeft, ChevronRight, Info, Loader2, MapPin } from "lucide-react";
import { useLocation } from "wouter";
import { navigate } from "wouter/use-browser-location";

function initials(name: string | null | undefined): string {
  const safe = (name ?? "??").trim() || "??";
  return safe
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function formatSelectedDayLabel(day: Date): string {
  return day.toLocaleDateString("en-SG", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function TodaysFieldSchedules() {
  const [pathname] = useLocation();
  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedDay, setSelectedDay] = useState(() => today);
  const [showEmptyTechnicians, setShowEmptyTechnicians] = useState(false);
  const safeDay = useMemo(() => coerceValidDay(selectedDay), [selectedDay]);
  const isSingaporeToday = isSameDay(safeDay, today);

  useEffect(() => {
    if (!isSingaporeToday) setShowEmptyTechnicians(false);
  }, [isSingaporeToday]);

  const { startDate, endDate } = useMemo(() => {
    const { startInclusive, endExclusive } = singaporeCalendarDayBounds(safeDay);
    return { startDate: startInclusive, endDate: endExclusive };
  }, [safeDay]);

  const { data: visitsRaw, isLoading: visitsLoading } = trpc.siteVisits.teamCalendarVisits.useQuery(
    { startDate, endDate },
    { keepPreviousData: true } as never,
  );
  const { data: leaveBlocksRaw } = trpc.siteVisits.teamCalendarLeaveBlocks.useQuery(
    { startDate, endDate },
    { keepPreviousData: true } as never,
  );
  const { data: allStaff, isLoading: staffLoading } = trpc.siteVisits.listStaff.useQuery();
  const fieldStaff = useMemo(
    () => (allStaff ?? []).filter((u) => FIELD_STAFF_PORTAL_ROLES.has(u.role)),
    [allStaff],
  );
  const isLoading = visitsLoading || staffLoading;

  const visits = useMemo(() => sanitizeVisits(visitsRaw), [visitsRaw]);
  const leaveBlocks = useMemo(() => sanitizeLeaveBlocks(leaveBlocksRaw), [leaveBlocksRaw]);

  const dayKey = dateKey(safeDay);
  const dayVisits = useMemo(
    () => visits.filter((v) => dateKey(visitPlacementDate(v)) === dayKey),
    [visits, dayKey],
  );
  const dayLeave = useMemo(
    () => leaveBlocksForDate(leaveBlocks, safeDay),
    [leaveBlocks, safeDay],
  );
  const scheduleRows = useMemo(
    () => buildFieldStaffScheduleRows(dayVisits, dayLeave, fieldStaff),
    [dayVisits, dayLeave, fieldStaff],
  );
  const todayPresentation = useMemo(
    () =>
      presentTodayTechnicianRows(scheduleRows, {
        applyTodayPresentation: isSingaporeToday,
        showEmptyTechnicians,
      }),
    [scheduleRows, isSingaporeToday, showEmptyTechnicians],
  );
  const visibleScheduleRows = todayPresentation.visibleRows;

  const hours = Array.from(
    { length: DISPATCH_END_HOUR - DISPATCH_START_HOUR + 1 },
    (_, i) => DISPATCH_START_HOUR + i,
  );
  const timelineWidth = DISPATCH_TIMELINE_WIDTH;

  const onVisitClick = (id: number) => {
    const returnTo = encodeURIComponent(pathname || "/");
    navigate(`/site-visits/id/${id}?returnTo=${returnTo}`, {
      state: { from: pathname || "/" },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Previous day"
            onClick={() => setSelectedDay((d) => addDays(d, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-[200px] text-center text-sm font-semibold text-slate-900 sm:min-w-[240px]">
            {formatSelectedDayLabel(safeDay)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Next day"
            onClick={() => setSelectedDay((d) => addDays(d, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            disabled={isSameDay(safeDay, today)}
            onClick={() => setSelectedDay(today)}
          >
            Today
          </Button>
          {todayPresentation.showToggle ? (
            <button
              type="button"
              aria-pressed={showEmptyTechnicians}
              aria-expanded={showEmptyTechnicians}
              onClick={() => setShowEmptyTechnicians((v) => !v)}
              className="h-8 rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-bold text-slate-700"
            >
              {showEmptyTechnicians ? HIDE_EMPTY_TECHNICIANS_LABEL : SHOW_EMPTY_TECHNICIANS_LABEL}
            </button>
          ) : null}
        </div>
        <Select value="day" disabled>
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue placeholder="Day View" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day View</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {DASHBOARD_SCHEDULE_LEGEND.map((item) => (
          <div key={item.key} className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <span className={cn("h-2 w-2 rounded-full", item.dotClass)} />
            {item.label}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading schedules…
        </div>
      ) : visibleScheduleRows.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-500">No field schedules for this day.</p>
      ) : (
        <div className="overflow-x-auto overscroll-x-contain rounded-lg border border-slate-200 bg-white">
          <div className="relative min-w-max" style={{ width: timelineWidth + 140 }}>
            <div
              className="sticky top-0 z-10 grid border-b border-slate-200 bg-slate-50"
              style={{ gridTemplateColumns: "140px 1fr" }}
            >
              <div className="border-r border-slate-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Technician
              </div>
              <div className="relative flex" style={{ width: timelineWidth }}>
                {hours.map((h) => {
                  const ampm = h < 12 ? "AM" : "PM";
                  const h12 = h % 12 === 0 ? 12 : h % 12;
                  return (
                    <div
                      key={h}
                      className="shrink-0 border-r border-slate-100 px-1 py-2 text-center text-[10px] font-semibold text-slate-500"
                      style={{ width: DISPATCH_PX_PER_HOUR }}
                    >
                      {h12} {ampm}
                    </div>
                  );
                })}
              </div>
            </div>

            {visibleScheduleRows.map((row) => {
              const rowKey = row.userId || row.userName;

              return (
                <div
                  key={rowKey}
                  className="grid border-b border-slate-100 last:border-b-0"
                  style={{ gridTemplateColumns: "140px 1fr" }}
                >
                  <div className="flex items-center gap-2 border-r border-slate-200 px-3 py-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700">
                      {initials(row.userName)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{row.userName}</p>
                      <p className="truncate text-[11px] capitalize text-slate-500">{row.role}</p>
                    </div>
                  </div>

                  <div
                    className="relative bg-[linear-gradient(to_right,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[length:72px_100%]"
                    style={{ width: timelineWidth, minHeight: DISPATCH_ROW_HEIGHT + 28 }}
                  >
                    {row.onLeave != null && row.visits.length === 0 && (
                      <div
                        className={cn(
                          "absolute top-2 flex items-center justify-center rounded-md text-xs font-semibold",
                          blockClassForLeave("dispatch"),
                        )}
                        style={{ left: 4, width: timelineWidth - 8, bottom: 12, top: 12 }}
                      >
                        On Leave
                      </div>
                    )}

                    {row.onLeave == null &&
                      layoutTimedVisits(row.visits).map(({ visit, startMins, endMins, column }) => {
                        if (!visit?.id) return null;
                        const left = dispatchLeftPx(startMins);
                        const width = Math.max(dispatchWidthPx(startMins, endMins), 72);
                        const top = 12 + column * 44;
                        const customer =
                          visit.customerName?.trim() || visit.customerAddress?.trim() || "—";

                        return (
                          <button
                            key={visit.id}
                            type="button"
                            onClick={() => onVisitClick(visit.id)}
                            className={cn(
                              "absolute z-10 overflow-hidden rounded-md px-2 py-1.5 text-left shadow-sm transition-opacity hover:opacity-95 active:opacity-90",
                              dashboardScheduleBarClass(visit),
                            )}
                            style={{
                              left,
                              width,
                              top,
                              maxWidth: Math.max(72, timelineWidth - left - 4),
                            }}
                          >
                            <p className="truncate text-[10px] font-semibold leading-tight opacity-90">
                              {formatMinutesLabel(startMins)} – {formatMinutesLabel(endMins)}
                            </p>
                            <div className="mt-0.5 flex items-center gap-1">
                              <span className="min-w-0 flex-1 truncate text-xs font-bold leading-tight">
                                {customer}
                              </span>
                              <MapPin className="h-3 w-3 shrink-0 opacity-70" />
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="flex items-start gap-1.5 text-[11px] text-slate-500">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Click on any visit bar to view visit details, notes, and site information.
      </p>
    </div>
  );
}
